<template>
  <div :class="columnClassName">
    <column-header :activeLayout="activeLayout" :columns="columns" :columnIndex="columnIndex" :components="components" :activeComponent="component" v-on:setActiveComponent="setActiveComponent"></column-header>
    <div :class="'tabulator-parent' + (hasFocus ? ' has-focus' : '')">
      <div class="editor-header">
        <h3><i class="fa fa-fw fa-layer-group"></i> {{ _('Storefront') }}: {{ _('Collections') }}</h3>
        <div class="header-controls small" v-if="canToggleSimplifiedCollections">
          <div class="form-check">
            <label class="form-check-label form-check-toggle">
              <input class="form-check-input" type="checkbox" v-model="advancedCollections" true-value="1" false-value="0" />
              <span v-if="!parseInt(advancedCollections)">{{ _('Show all details') }}</span>
              <span v-else>{{ _('All details shown') }}</span>
            </label>
          </div>
        </div>
      </div>
      <editor-tabs id="tabNavi" v-show="!hasFocus" :tabs="tabList" :selected="selectedTab" :id-prefix="tabIdPrefix" v-on:select="tab" />
      <div class="tab-content">
        <transition-group name="fade">
          <div v-bind="tabPanel('collections')" class="tab-pane active" id="collections" v-if="isTabActive('collections')" key="collections">
            <div class="row">
              <div class="col-xs-6">
                <h3>{{ _('Collections') }}</h3>
              </div>
              <div class="col-xs-6">
                <button type="button" class="btn btn-success pull-right" v-on:click="addCollection"><i class="fa fa-fw fa-plus"></i> {{ _('Add collection') }}</button>
              </div>
            </div>
            <Datatable :url="collectionsUrl" :options="collectionsTableOptions" ref="collectionsDatatable" v-on:data="collectionsLoaded" />
          </div>
          <div v-bind="tabPanel('groups')" class="tab-pane active" id="groups" v-if="isTabActive('groups') && advancedCollections" key="groups">
            <div class="row">
              <div class="col-xs-6">
                <h3>{{ _('User groups') }}</h3>
              </div>
              <div class="col-xs-6">
                <button type="button" class="btn btn-success pull-right" v-on:click="addGroup"><i class="fa fa-fw fa-plus"></i> {{ _('Add user group') }}</button>
              </div>
            </div>
            <Datatable :url="groupsUrl" :options="groupsTableOptions" ref="groupsDatatable" />
          </div>
          <div v-bind="tabPanel('users')" class="tab-pane active" id="users" v-if="isTabActive('users')" key="users">
            <div class="row">
              <div class="col-xs-6">
                <h3>{{ _('Collection users') }}</h3>
              </div>
              <div class="col-xs-6">
                <button type="button" class="btn btn-success pull-right" v-on:click="inviteUser"><i class="fa fa-fw fa-envelope"></i> {{ _('Invite user') }}</button>
              </div>
            </div>
            <div class="list-filter-row">
              <div class="list-filter-col filtercol">
                <div class="form-group">
                  <div class="input-group">
                    <input class="form-control" type="text" v-model="userSearch" :placeholder="_('Keyword search')" />
                    <span class="input-group-btn">
                      <button type="button" :class="'btn btn-success' + (listFilterVisible ? ' active' : '')" v-on:click="listFilterVisible = !listFilterVisible">
                        <i :class="'fa fa-fw ' + (listFilterVisible ? 'fa-caret-up' : 'fa-sliders')"></i>
                      </button>
                    </span>
                  </div>
                </div>
              </div>
              <div v-if="listFilterVisible" class="list-filter-col filtercol">
                <div class="form-group">
                  <select class="form-control" v-model="userlevelFilter">
                    <option value="all">{{ _('All levels') }}</option>
                    <option value="1">{{ _('Private sales channel user') }}</option>
                    <option value="2">{{ _('POS user') }}</option>
                    <option value="3">{{ _('Standard user') }}</option>
                    <option value="7">{{ _('Administrator') }}</option>
                    <option value="10">{{ _('System Administrator') }}</option>
                  </select>
                </div>
              </div>
            </div>
            <Datatable :url="usersUrl" :search="userSearch" :userlevel-filter="userlevelFilter" :options="usersTableOptions" ref="usersDatatable" />
          </div>
          <div v-bind="tabPanel('invites')" class="tab-pane active" id="invites" v-if="isTabActive('invites')" key="invites">
            <div class="row">
              <div class="col-xs-6">
                <h3>{{ _('Sent invites') }}</h3>
              </div>
              <div class="col-xs-6">
                <button type="button" class="btn btn-success pull-right" v-on:click="inviteUser"><i class="fa fa-fw fa-envelope"></i> {{ _('Invite user') }}</button>
              </div>
            </div>
            <Datatable :url="invitesUrl" :options="invitesTableOptions" ref="invitesDatatable" />
          </div>
        </transition-group>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { DateTime } from 'luxon'
import striptags from 'striptags'
import components from '~/lib/components'
import tabs from '~/lib/tabs'
import { Datatable } from '#components'
import InviteUserModal from '~/components/modals/InviteUserModal'

export default {
  mixins: [components, tabs],
  components: { Datatable },
  props: {
    focus: { default: null }
  },
  data () {
    const _ = useNuxtApp().$translate
    const $this = this
    return {
      collections: [],
      collectionsTableOptions: {
        columns: [
          { title: _('Name'), field: 'name', widthGrow: 2 },
          {
            title: _('Description'),
            field: 'description',
            widthGrow: 3,
            formatter (cell) {
              const el = cell.getElement()
              el.style.whiteSpace = 'nowrap'
              el.style.overflow = 'hidden'
              el.style.textOverflow = 'ellipsis'
              return striptags(cell.getValue() || '')
            },
            tooltip (e, cell) {
              return striptags(cell.getValue() || '')
            }
          },
          { title: _('Categories'), field: 'categorycount', width: 160, hozAlign: 'right' },
          {
            title: _('User groups'),
            field: 'groupcount',
            width: 130,
            hozAlign: 'right',
            formatter (cell) {
              const data = cell.getRow().getData()
              return parseInt($this.advancedCollections) ? data.groupcount : data.usercount
            }
          },
          {
            title: _('Access products'),
            field: 'accessproductcount',
            width: 140,
            hozAlign: 'right',
            headerSort: false
          }
        ],
        initialSort: [{ column: 'name', dir: 'asc' }],
        rowClick (e, row) {
          const data = row.getData()
          $this.openCollectionDetails({ id: data.id, name: data.name })
        }
      },
      groupsTableOptions: {
        columns: [
          { title: _('Name'), field: 'name', widthGrow: 2 },
          { title: _('Description'), field: 'description', widthGrow: 3 },
          {
            title: _('All users'),
            field: 'allusers',
            width: 110,
            hozAlign: 'center',
            formatter: (cell) => parseInt(cell.getValue()) ? '<i class="fa fa-check"></i>' : ''
          }
        ],
        initialSort: [{ column: 'allusers', dir: 'desc' }],
        rowClick (e, row) {
          const data = row.getData()
          $this.openGroupDetails({ id: data.id, name: data.name })
        }
      },
      userSearch: '',
      userlevelFilter: 'all'
    }
  },
  computed: {
    tabList () {
      return this.editorTabs([
        { id: 'collections', label: this._('Collections') },
        { id: 'groups', label: this._('User groups'), hidden: !this.advancedCollections },
        { id: 'users', label: this._('Collection users') },
        { id: 'invites', label: this._('Sent invites') }
      ])
    },
    collectionsUrl () {
      return this.baseDomain + this.shopId + '/collections.json?apikey=' + this.apiKey
    },
    groupsUrl () {
      return this.baseDomain + this.shopId + '/usergroups.json?apikey=' + this.apiKey
    },
    usersUrl () {
      return this.baseDomain + this.shopId + '/users.json?apikey=' + this.apiKey
    },
    invitesUrl () {
      return this.baseDomain + this.shopId + '/userinvitations.json?apikey=' + this.apiKey
    },
    advancedCollections: {
      get () {
        return this.$store.getters.advancedCollections
      },
      set (newValue) {
        return this.$store.dispatch('storeAdvancedCollections', parseInt(newValue))
      }
    },
    canToggleSimplifiedCollections () {
      if (!this.advancedCollections) return true
      if (!this.collections || !this.collections.length) return true
      return this.collections.every(c => !c.groupcount || parseInt(c.groupcount) <= 1)
    },
    hasFocus () {
      return !!this.focus
    },
    usersTableOptions () {
      const $this = this
      const _ = this._
      return {
        persistenceSuffix: 'collection-users',
        columns: [
          { title: _('Name'), field: 'name', widthGrow: 2 },
          { title: _('Email'), field: 'email', widthGrow: 3 },
          {
            title: _('Level'),
            field: 'userlevel',
            widthGrow: 1,
            formatter (cell) {
              const levels = {
                1: _('Private sales channel user'),
                2: _('POS user'),
                3: _('Standard user'),
                7: _('Administrator'),
                10: _('System Administrator')
              }
              return levels[parseInt(cell.getValue())] || cell.getValue()
            }
          },
          {
            title: _('User groups'),
            field: 'groupnames',
            widthGrow: 2,
            visible: !!parseInt(this.advancedCollections)
          },
          { title: _('Collections'), field: 'collectionnames', widthGrow: 2 }
        ],
        initialSort: [{ column: 'name', dir: 'asc' }],
        rowClick (e, row) {
          const data = row.getData()
          $this.openUserDetails({ id: data.id, name: data.name })
        }
      }
    },
    invitesGroupColumnTitle () {
      return parseInt(this.advancedCollections) ? this._('User groups') : this._('Collections')
    },
    collectionsCountColumnTitle () {
      return parseInt(this.advancedCollections) ? this._('User groups') : this._('Users')
    },
    invitesTableOptions () {
      const $this = this
      return {
        columns: [
          { title: this._('Name'), field: 'name', widthGrow: 2 },
          { title: this._('Email'), field: 'email', widthGrow: 3 },
          { title: this.invitesGroupColumnTitle, field: 'groupnames', widthGrow: 2 },
          { title: this._('Invited by'), field: 'invitedbyname', widthGrow: 2 },
          {
            title: this._('Start date'),
            field: 'startdate',
            width: 120,
            formatter (cell) {
              return $this.formatInviteMembershipDate(cell.getValue())
            }
          },
          {
            title: this._('End date'),
            field: 'enddate',
            width: 120,
            formatter (cell) {
              return $this.formatInviteMembershipDate(cell.getValue())
            }
          },
          {
            title: this._('Expires'),
            field: 'expires',
            width: 170,
            formatter (cell) {
              const value = cell.getValue()
              if (!value) return ''
              return DateTime.fromSQL(value).toFormat($this.dateFormat + ' ' + $this.timeFormat)
            }
          },
          {
            title: this._('Status'),
            field: 'expired',
            width: 110,
            formatter (cell) {
              return parseInt(cell.getValue()) ? $this._('Expired') : $this._('Pending')
            }
          },
          {
            title: '',
            field: 'actions',
            width: 50,
            hozAlign: 'center',
            headerSort: false,
            formatter () {
              return '<i class="fa fa-fw fa-trash text-danger" style="cursor:pointer"></i>'
            },
            cellClick (e, cell) {
              e.stopPropagation()
              $this.revokeInviteDialog(cell.getRow().getData())
            }
          }
        ],
        initialSort: [{ column: 'expires', dir: 'asc' }]
      }
    }
  },
  watch: {
    advancedCollections () {
      if (!parseInt(this.advancedCollections) && this.activeTab === 'groups') {
        this.tab('collections')
      }
      this.updateGroupColumnTitles()
    }
  },
  methods: {
    collectionsLoaded (data) {
      this.collections = data || []
    },
    addCollection () {
      this.openCollectionDetails({ id: null })
    },
    addGroup () {
      this.openGroupDetails({ id: null })
    },
    openCollectionDetails (collection) {
      this.EventBus.$emit('showDetailsColumn', {
        shopId: this.shopId,
        component: 'CollectionDetails',
        title: collection.id ? collection.name : this._('New collection'),
        width: 6,
        data: { collectionId: collection.id }
      })
    },
    openGroupDetails (group) {
      this.EventBus.$emit('showDetailsColumn', {
        shopId: this.shopId,
        component: 'GroupDetails',
        title: group.id ? group.name : this._('New user group'),
        width: 6,
        data: { groupId: group.id }
      })
    },
    openUserDetails (user) {
      this.EventBus.$emit('showDetailsColumn', {
        shopId: this.shopId,
        component: 'CollectionUserDetails',
        title: user.name || this._('User'),
        width: 6,
        data: { userId: user.id }
      })
    },
    inviteUser () {
      this.showModal('invite-user', InviteUserModal, {
        props: {
          baseDomain: this.baseDomain,
          shopId: this.shopId,
          apiKey: this.apiKey,
          locale: this.locale,
          advancedCollections: parseInt(this.advancedCollections),
          groupsUrl: this.groupsUrl
        },
        width: 560,
        backgroundEvents: {
          added: () => {
            this.reloadUsersList()
            this.EventBus.$emit('collectionUsersChanged')
          }
        },
        handlers: {
          closed: () => this.reloadInvitesList()
        }
      })
    },
    formatInviteMembershipDate (value) {
      if (!value) return ''
      const iso = String(value).slice(0, 10)
      const parsed = DateTime.fromISO(iso)
      return parsed.isValid ? parsed.toFormat(this.dateFormat) : iso
    },
    revokeInviteDialog (invite) {
      this.showDialog({
        title: this._('Revoke this invitation?'),
        width: 600,
        buttons: [
          {
            title: this._('Cancel'),
            icon: 'ban',
            handler: () => {}
          },
          {
            title: this._('Revoke'),
            class: 'danger',
            icon: 'trash',
            handler: () => this.revokeInvite(invite)
          }
        ]
      })
    },
    async revokeInvite (invite) {
      try {
        await axios.delete(this.baseDomain + this.shopId + '/userinvitations/' + invite.id + '.json?apikey=' + this.apiKey)
      } catch (e) {
        this.showError(e)
        return
      }
      this.reloadInvitesList()
    },
    reloadCollectionsList (message) {
      if (this.$refs && this.$refs.collectionsDatatable) {
        this.$refs.collectionsDatatable.reload(false, message || {})
      }
    },
    reloadGroupsList (message) {
      if (this.$refs && this.$refs.groupsDatatable) {
        this.$refs.groupsDatatable.reload(false, message || {})
      }
    },
    reloadInvitesList () {
      if (this.$refs && this.$refs.invitesDatatable) {
        this.$refs.invitesDatatable.reload()
      }
    },
    reloadUsersList () {
      if (this.$refs && this.$refs.usersDatatable) {
        this.$refs.usersDatatable.reload()
      }
    },
    tableColumn (datatable, field) {
      const tabulator = datatable && datatable.tabulator
      if (!tabulator || tabulator.destroyed) {
        return null
      }
      try {
        const columns = tabulator.getColumns()
        if (!columns.some(col => col.getField() === field)) {
          return null
        }
        return tabulator.getColumn(field)
      } catch (e) {
        return null
      }
    },
    updateGroupColumnTitles () {
      const title = this.invitesGroupColumnTitle
      const invitesColumn = this.tableColumn(this.$refs?.invitesDatatable, 'groupnames')
      if (invitesColumn) {
        invitesColumn.updateDefinition({ title })
      }
      const collectionsColumn = this.tableColumn(this.$refs?.collectionsDatatable, 'groupcount')
      if (collectionsColumn) {
        collectionsColumn.updateDefinition({ title: this.collectionsCountColumnTitle })
      }
      const usersGroupColumn = this.tableColumn(this.$refs?.usersDatatable, 'groupnames')
      if (usersGroupColumn) {
        if (parseInt(this.advancedCollections)) {
          usersGroupColumn.show()
        } else {
          usersGroupColumn.hide()
        }
      }
    }
  },
  activated () {
    if (this.component && this.component.initData && this.component.initData.activateTab) {
      this.tab(this.component.initData.activateTab)
    }
  },
  mounted () {
    if (this.component && this.component.initData && this.component.initData.activateTab) {
      this.tab(this.component.initData.activateTab)
    }
    if (!this.events) {
      const events = {}
      events.collectionUpdate = (message) => this.reloadCollectionsList(message)
      events.usergroupUpdate = (message) => this.reloadGroupsList(message)
      events.userUpdate = () => this.reloadUsersList()
      events.collectionUsersChanged = () => this.reloadUsersList()
      this.events = events
    }
    this.enableEventBus()
    this.$nextTick(() => this.updateGroupColumnTitles())
  }
}
</script>